import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Baby as BabyIcon, Moon, Drop, Trash, Plus } from "@phosphor-icons/react";

const KINDS = {
  feed: { icon: BabyIcon, label: "Feed", color: "text-terracotta", bg: "bg-terracotta/10" },
  sleep: { icon: Moon, label: "Sleep", color: "text-forest", bg: "bg-forest/10" },
  diaper: { icon: Drop, label: "Diaper", color: "text-saffron", bg: "bg-saffron/10" },
};

export default function Newborn() {
  const [logs, setLogs] = useState([]);
  const [kindFilter, setKindFilter] = useState("");
  const [quickKind, setQuickKind] = useState("feed");
  const [detail, setDetail] = useState({ duration_min: "", amount_ml: "", side: "", type: "wet", notes: "" });

  const load = async () => {
    const r = await api.get(`/prep/newborn/logs?days=7${kindFilter ? `&kind=${kindFilter}` : ""}`);
    setLogs(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kindFilter]);

  const quickLog = async () => {
    try {
      await api.post("/prep/newborn/log", {
        kind: quickKind,
        log_time: new Date().toISOString(),
        detail: {
          duration_min: detail.duration_min ? Number(detail.duration_min) : null,
          amount_ml: detail.amount_ml ? Number(detail.amount_ml) : null,
          side: detail.side || null,
          type: quickKind === "diaper" ? detail.type : null,
          notes: detail.notes,
        },
      });
      toast.success("Logged.");
      setDetail({ duration_min: "", amount_ml: "", side: "", type: "wet", notes: "" });
      load();
    } catch { toast.error("Could not log."); }
  };

  const del = async (id) => { await api.delete(`/prep/newborn/log/${id}`); load(); };

  const today = new Date().toDateString();
  const todayLogs = logs.filter((l) => new Date(l.log_time).toDateString() === today);
  const counts = {
    feed: todayLogs.filter((l) => l.kind === "feed").length,
    sleep: todayLogs.filter((l) => l.kind === "sleep").length,
    diaper: todayLogs.filter((l) => l.kind === "diaper").length,
  };

  return (
    <div className="space-y-8" data-testid="newborn-page">
      <header>
        <p className="overline text-saffron mb-2">Fourth trimester</p>
        <h1 className="font-serif font-semibold text-4xl sm:text-5xl">Newborn dashboard</h1>
        <p className="text-ink-700 mt-2">Feeds, sleeps, diapers — the rhythm of these days.</p>
      </header>

      <div className="grid grid-cols-3 gap-4" data-testid="newborn-counts">
        {Object.entries(KINDS).map(([k, info]) => (
          <div key={k} className="vansha-card p-6 text-center" data-testid={`count-${k}`}>
            <info.icon weight="thin" size={28} className={`${info.color} mx-auto mb-2`} />
            <p className="font-serif text-4xl">{counts[k]}</p>
            <p className="overline">{info.label}s today</p>
          </div>
        ))}
      </div>

      <div className="vansha-card p-6 space-y-5" data-testid="quick-log">
        <div className="flex gap-2 flex-wrap">
          {Object.entries(KINDS).map(([k, info]) => (
            <button key={k} onClick={() => setQuickKind(k)} className={`px-4 py-2 rounded-full text-sm font-medium ${quickKind === k ? "bg-terracotta text-white" : "border border-ink-20 text-ink-700"}`} data-testid={`kind-${k}`}>
              <info.icon weight="thin" size={16} className="inline mr-1" /> {info.label}
            </button>
          ))}
        </div>
        {quickKind === "feed" && (
          <div className="grid sm:grid-cols-3 gap-4">
            <div><label className="overline mb-2 block">Side</label>
              <select value={detail.side} onChange={(e) => setDetail({ ...detail, side: e.target.value })} className="input-line bg-transparent" data-testid="feed-side">
                <option value="">—</option><option value="left">Left</option><option value="right">Right</option><option value="both">Both</option><option value="bottle">Bottle</option>
              </select>
            </div>
            <div><label className="overline mb-2 block">Duration (min)</label><input type="number" value={detail.duration_min} onChange={(e) => setDetail({ ...detail, duration_min: e.target.value })} className="input-line" data-testid="feed-duration" /></div>
            <div><label className="overline mb-2 block">Amount (ml)</label><input type="number" value={detail.amount_ml} onChange={(e) => setDetail({ ...detail, amount_ml: e.target.value })} className="input-line" data-testid="feed-amount" /></div>
          </div>
        )}
        {quickKind === "sleep" && (
          <div><label className="overline mb-2 block">Duration (min)</label><input type="number" value={detail.duration_min} onChange={(e) => setDetail({ ...detail, duration_min: e.target.value })} className="input-line max-w-xs" data-testid="sleep-duration" /></div>
        )}
        {quickKind === "diaper" && (
          <div><label className="overline mb-2 block">Type</label>
            <select value={detail.type} onChange={(e) => setDetail({ ...detail, type: e.target.value })} className="input-line bg-transparent max-w-xs" data-testid="diaper-type">
              <option value="wet">Wet</option><option value="dirty">Dirty</option><option value="both">Both</option>
            </select>
          </div>
        )}
        <div><label className="overline mb-2 block">Notes</label><input value={detail.notes} onChange={(e) => setDetail({ ...detail, notes: e.target.value })} className="input-line" data-testid="log-notes" /></div>
        <div className="flex justify-end">
          <button onClick={quickLog} className="btn-primary" data-testid="log-now"><Plus weight="thin" size={16} /> Log now</button>
        </div>
      </div>

      <section>
        <div className="flex justify-between items-end flex-wrap gap-3 mb-4">
          <h2 className="font-serif text-2xl">History (last 7 days)</h2>
          <div className="flex gap-2">
            <button onClick={() => setKindFilter("")} className={`text-sm px-3 py-1 rounded-full ${!kindFilter ? "bg-terracotta text-white" : "border border-ink-20"}`} data-testid="filter-all">All</button>
            {Object.keys(KINDS).map((k) => (
              <button key={k} onClick={() => setKindFilter(k)} className={`text-sm px-3 py-1 rounded-full ${kindFilter === k ? "bg-terracotta text-white" : "border border-ink-20"}`} data-testid={`filter-${k}`}>{KINDS[k].label}</button>
            ))}
          </div>
        </div>
        {logs.length === 0 ? <p className="text-ink-500" data-testid="logs-empty">No entries yet.</p> : (
          <div className="space-y-2">
            {logs.map((l) => {
              const info = KINDS[l.kind];
              const Icon = info?.icon || BabyIcon;
              return (
                <div key={l.id} className="vansha-card p-4 flex items-center gap-4" data-testid={`log-${l.id}`}>
                  <div className={`w-10 h-10 rounded-full ${info?.bg} flex items-center justify-center`}>
                    <Icon weight="thin" size={20} className={info?.color} />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium capitalize">{l.kind}</p>
                    <p className="text-xs text-ink-500">{new Date(l.log_time).toLocaleString()}</p>
                    {l.detail && (
                      <p className="text-xs text-ink-700 mt-1">
                        {l.detail.duration_min ? `${l.detail.duration_min} min · ` : ""}
                        {l.detail.amount_ml ? `${l.detail.amount_ml} ml · ` : ""}
                        {l.detail.side ? `${l.detail.side} · ` : ""}
                        {l.detail.type ? `${l.detail.type} · ` : ""}
                        {l.detail.notes || ""}
                      </p>
                    )}
                  </div>
                  <button onClick={() => del(l.id)} className="btn-ghost p-2"><Trash weight="thin" size={14} /></button>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
